import Image from 'next/image';
import { useMemo } from 'react';
import { NetworkData, NETWORKS } from 'src/common/types/network';

import { KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';
import { maskAddressValue } from '@/utils/format';

import { Trigger } from '../../domain';

interface Props {
  trigger: Trigger;
}

const DENOMINATOR_LABELS: Record<string, string> = {
  token0: 'Token 0',
  token1: 'Token 1',
};

/**
 * Separate from the V3 view for two reasons: there is no strategy row to show — V4 is active
 * liquidity only, so the distribution weights never apply — and the pool comes from the trigger
 * context rather than being parsed out of the signature, since a V4 pool id is not an address.
 */
export function UniswapLiquidityV4TriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const poolId = (trigger.context?.pool_id as string | undefined) ?? '';
  const chainId = trigger.context?.chain_id;
  const tokenDenominator = trigger.context?.token_denominator as string | undefined;

  const networkDisplay = useMemo(() => {
    if (chainId == null) return '-';

    const network = NETWORKS.find((n: NetworkData) => n.chainId === Number(chainId));
    if (!network) return '-';

    const chainIconUrl = getChainIconUrl(Number(chainId));

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={network.name} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{network.name.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [chainId, getChainIconUrl]);

  const poolDisplay = useMemo(() => {
    if (!poolId) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Snapshots of LP holders of
        </Text>
        {/* Not an AddressChip: a V4 pool id has no block explorer page to link to. */}
        <Text as="span" variant="body-sm">
          {maskAddressValue(poolId) ?? '-'}
        </Text>
      </div>
    );
  }, [poolId]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value={poolDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow
        label="Position denominator"
        value={tokenDenominator ? (DENOMINATOR_LABELS[tokenDenominator] ?? tokenDenominator) : '-'}
      />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
